import {
  resolveEmotion,
  scoreToBand,
  type SentimentTrajectory as Trajectory,
  type ValenceBand,
} from './emotionConfig';

interface SentimentTrajectoryProps {
  trajectory?: Trajectory;
}

/**
 * Sentiment sparkline — hand-drawn SVG, 282×44, fixed.
 *
 * Recharts was the cause of every sentiment defect on screen: a clipped end
 * point, a stroke that escaped the column, a tooltip card covering a 44px plot,
 * and no way to draw a neutral band or per-point squares. Four points do not
 * need a chart library, and the column width is fixed (326 − 22 − 22 = 282), so
 * there is nothing for a ResponsiveContainer to respond to.
 *
 * Geometry, from the design doc:
 *   x = 8 + i × (282 − 16) / (n − 1);  n === 1 → 141 (centre)
 *   y = 22 − score × 20, clamped to [2, 42]
 * Score 0 sits on the dashed midline; ±0.2 is the neutral band's edge, so the
 * band is the 8px rect at y = 18.
 */

export const PLOT_W = 282;
export const PLOT_H = 44;
const PAD_X = 8;

/** Exported for the arithmetic check: −0.62 → 34.4, +0.41 → 13.8. */
export function scoreToY(score: number): number {
  return Math.min(42, Math.max(2, 22 - score * 20));
}

export function scoreToX(index: number, count: number): number {
  if (count <= 1) return PLOT_W / 2;
  return PAD_X + (index * (PLOT_W - PAD_X * 2)) / (count - 1);
}

const BAND: Record<ValenceBand, { stroke: string; line: string; wash: string; ink: string; glyph: string }> = {
  positive: {
    stroke: 'var(--co-green)',
    line: 'var(--co-green-line)',
    wash: 'var(--co-green-wash)',
    ink: 'var(--co-green-ink)',
    glyph: '◔',
  },
  neutral: {
    stroke: 'var(--co-ink-4)',
    line: 'var(--co-line)',
    wash: 'var(--co-raised)',
    ink: 'var(--co-ink-3)',
    glyph: '◑',
  },
  negative: {
    stroke: 'var(--co-terracotta)',
    line: 'var(--co-terracotta-line)',
    wash: 'var(--co-terracotta-wash)',
    ink: 'var(--co-terracotta-ink)',
    glyph: '▲',
  },
};

/** Signed, 2dp, with a real minus sign. */
function fmtScore(n: number): string {
  const s = Math.abs(n).toFixed(2);
  return n < 0 ? `−${s}` : `+${s}`;
}

/** `DD MMM`, or `TODAY`. */
function fmtDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  if (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  ) {
    return 'TODAY';
  }
  return d
    .toLocaleDateString(undefined, { day: '2-digit', month: 'short' })
    .toUpperCase();
}

export function SentimentTrajectory({ trajectory }: SentimentTrajectoryProps) {
  const points = trajectory?.points ?? [];
  if (points.length === 0) return null;

  const current = trajectory?.current ?? points[points.length - 1];

  // Headline emotion: when the conversation contains a meaningfully negative
  // moment, surface the customer's WORST sentiment — a disappointed customer
  // whose last message happens to be neutral must NOT read as "Neutral".
  // Kept verbatim from the previous implementation.
  const NEGATIVE_SCORE = -0.2;
  const worstNegative = points.reduce<(typeof points)[number] | null>(
    (acc, p) =>
      typeof p.score === 'number' &&
      p.score <= NEGATIVE_SCORE &&
      (acc === null || p.score < acc.score)
        ? p
        : acc,
    null,
  );
  // An "angry → satisfied" recovery should read as satisfied. Only when the
  // conversation ended neutral/negative do we surface the worst moment, so a
  // trailing "thanks" can't mask real disappointment.
  const endedPositive =
    typeof current?.score === 'number' && scoreToBand(current.score) === 'positive';
  const headline = endedPositive ? current : worstNegative ?? current;

  const headlineBand = BAND[scoreToBand(headline?.score ?? current?.score ?? 0)];
  const currentBand = BAND[scoreToBand(current?.score ?? 0)];
  // The display label from the shared emotion vocabulary — 'Disappointment',
  // not the raw 'disappointment' key. CSS uppercases it for the chip.
  const emotionLabel = resolveEmotion(headline?.dominantEmotion).label;

  const header = (
    <div className="mt-[11px] flex items-center gap-2">
      <span className="font-mono text-[9px] font-medium uppercase tracking-[.16em] text-[var(--co-ink-4)]">
        Sentiment
      </span>
      <div className="flex-1" />
      <span
        style={{
          borderColor: headlineBand.line,
          background: headlineBand.wash,
          color: headlineBand.ink,
        }}
        className="inline-flex items-center gap-[5px] border px-[5px] py-[3px] font-mono text-[9.5px] font-medium uppercase tracking-[.14em]"
      >
        <span aria-hidden="true">{headlineBand.glyph}</span>
        {emotionLabel}
      </span>
    </div>
  );

  // One reading is a chip and a word — a two-pixel line is not a trajectory.
  if (points.length === 1) {
    return (
      <div>
        {header}
        <div className="mt-[5px] font-mono text-[9px] tracking-[.1em] text-[var(--co-line-strong)]">
          SINGLE READING
        </div>
      </div>
    );
  }

  const pts = points.map((p, i) => {
    const score = typeof p.score === 'number' ? p.score : 0;
    return {
      x: scoreToX(i, points.length),
      y: scoreToY(score),
      band: BAND[scoreToBand(score)],
    };
  });

  const first = points[0];
  const last = points[points.length - 1];

  return (
    <div>
      {header}

      <svg
        width={PLOT_W}
        height={PLOT_H}
        viewBox={`0 0 ${PLOT_W} ${PLOT_H}`}
        className="mt-2 block"
        role="img"
        aria-label={`Sentiment trajectory: ${first?.dominantEmotion ?? 'neutral'} to ${
          last?.dominantEmotion ?? 'neutral'
        } across ${points.length} scored messages`}
      >
        {/* ±0.2 neutral band, and the zero midline it straddles. */}
        <rect x="0" y="18" width={PLOT_W} height="8" fill="var(--co-neutral-band)" />
        <line
          x1="0"
          y1="22"
          x2={PLOT_W}
          y2="22"
          stroke="var(--co-line)"
          strokeWidth="1"
          strokeDasharray="2 3"
        />
        <polyline
          points={pts.map((p) => `${p.x},${p.y}`).join(' ')}
          fill="none"
          stroke={headlineBand.stroke}
          strokeWidth="1.5"
        />
        {/* Each square takes its OWN band; only the line takes the headline's. */}
        {pts.map((p, i) => {
          const s = i === pts.length - 1 ? 7 : 5;
          return (
            <rect
              key={i}
              x={p.x - s / 2}
              y={p.y - s / 2}
              width={s}
              height={s}
              fill={p.band.stroke}
            />
          );
        })}
      </svg>

      <div className="mt-[5px] flex items-center font-mono text-[9px] tracking-[.1em] text-[var(--co-line-strong)]">
        <span>
          {fmtDate(first?.timestamp)} · {fmtScore(typeof first?.score === 'number' ? first.score : 0)}
        </span>
        <div className="flex-1" />
        <span>{points.length} SCORED</span>
        <div className="flex-1" />
        <span style={{ color: currentBand.ink }}>
          {fmtDate(last?.timestamp)} · {fmtScore(typeof last?.score === 'number' ? last.score : 0)}
        </span>
      </div>
    </div>
  );
}

export default SentimentTrajectory;
